import type { ConfiguredImageProviders, ImageModifiers } from '@nuxt/image';

/**
 * Copied from `@nuxt/image` v2.0.0 — `runtime/utils/props.ts`, `runtime/utils/index.ts`,
 * `runtime/utils/performance.ts` — none of which the package exports.
 * `index.vue` is a fork of its `NuxtPicture` and needs the very same behaviour.
 *
 * Single deliberate change: `modifiers` drops the provider-conditional part of the upstream type. That
 * conditional resolves through `ConfiguredImageProviders`, an interface published empty and never augmented
 * here, so it always fell back to `Record<string, unknown>` and clashed with the IPX modifier types.
 */
export interface BaseImageProperties<Provider extends keyof ConfiguredImageProviders> {
  // input source
  src?: string;

  // modifiers
  format?: string;
  quality?: string | number;
  background?: string;
  fit?: string;
  modifiers?: Partial<Omit<ImageModifiers, 'format' | 'quality' | 'background' | 'fit'>>;

  // options
  preset?: string;
  provider?: Provider;

  sizes?: string | Record<string, string | number>;
  densities?: string;
  preload?: boolean | { fetchPriority: 'auto' | 'high' | 'low' };

  // <img> attributes
  width?: string | number;
  height?: string | number;
  crossorigin?: 'anonymous' | 'use-credentials' | boolean;

  // csp
  nonce?: string;
}

export const useImageProperties = <Provider extends keyof ConfiguredImageProviders>(
  properties: BaseImageProperties<Provider>,
) => {
  const $img = useImage();

  const providerOptions = computed(() => ({
    provider: properties.provider,
    preset: properties.preset,
  }));

  const normalizedAttributes = computed(() => ({
    width: parseSize(properties.width),
    height: parseSize(properties.height),
    crossorigin: properties.crossorigin === true ? ('anonymous' as const) : properties.crossorigin || undefined,
    nonce: properties.nonce,
  }));

  const imageModifiers = computed(() => {
    return {
      ...properties.modifiers,
      width: properties.width,
      height: properties.height,
      format: properties.format,
      quality: properties.quality || $img.options.quality,
      background: properties.background,
      fit: properties.fit,
    } satisfies Partial<ImageModifiers>;
  });

  return { providerOptions, normalizedAttributes, imageModifiers };
};

export const markFeatureUsage = (featureName: string) => {
  performance?.mark?.('mark_feature_usage', { detail: { feature: featureName } });
};

const parseSize = (input: string | number = ''): number | undefined => {
  if (typeof input === 'number') {
    return input;
  }
  if (/^\d+$/g.test(input.replace('px', ''))) {
    return Number.parseInt(input, 10);
  }
  return undefined;
};
